import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import api from '../services/api';
import { Key, Shield, CheckCircle, XCircle, Eye, EyeOff, AlertCircle, Loader2, ExternalLink } from 'lucide-react';
import efoodLogo from '../assets/logos/logo_efood.png';

interface LaasMileConfigData {
  laas_mile_enabled: boolean;
  laas_mile_client_id: string | null;
  laas_mile_key_id: string | null;
  laas_mile_scope: string | null;
  laas_mile_venue_id: string | null;
  has_private_key: boolean;
}

const LaasMileConfig: React.FC = () => {
  const { t } = useTranslation(['settings', 'common']);
  const { store } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState<LaasMileConfigData>({
    laas_mile_enabled: false,
    laas_mile_client_id: '',
    laas_mile_key_id: '',
    laas_mile_scope: '',
    laas_mile_venue_id: '',
    has_private_key: false
  });
  const [privateKey, setPrivateKey] = useState('');
  const [showPrivateKey, setShowPrivateKey] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (store?.id) {
      fetchConfig();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store?.id]);

  const fetchConfig = async () => {
    if (!store?.id) return;
    
    setLoading(true);
    try {
      const response = await api.get(`/laas-mile/${store.id}/config`);
      setConfig({
        laas_mile_enabled: response.data.laas_mile_enabled || false,
        laas_mile_client_id: response.data.laas_mile_client_id || '',
        laas_mile_key_id: response.data.laas_mile_key_id || '',
        laas_mile_scope: response.data.laas_mile_scope || '',
        laas_mile_venue_id: response.data.laas_mile_venue_id || '',
        has_private_key: response.data.has_private_key || false
      });
    } catch (error) {
      console.error('Error fetching Laas Mile config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!store?.id) return;
    
    setSaving(true);
    setMessage(null);
    
    try {
      const updateData: any = {
        laas_mile_enabled: config.laas_mile_enabled,
        laas_mile_client_id: config.laas_mile_client_id,
        laas_mile_key_id: config.laas_mile_key_id,
        laas_mile_scope: config.laas_mile_scope,
        laas_mile_venue_id: config.laas_mile_venue_id
      };
      
      // Only include private key if it was changed
      if (privateKey) {
        updateData.laas_mile_private_key = privateKey;
      }
      
      await api.put(`/laas-mile/${store.id}/config`, updateData);
      
      setMessage({ type: 'success', text: t('settings:laasMile.settingsSavedSuccess') });
      setPrivateKey(''); // Clear the private key field
      fetchConfig(); // Refresh to get updated has_private_key
    } catch (error: any) {
      console.error('Error saving Laas Mile config:', error);
      setMessage({ type: 'error', text: error.response?.data?.error || t('settings:laasMile.settingsSaveError') });
    } finally {
      setSaving(false);
    }
  };

  const handleToggleEnabled = async () => {
    if (!store?.id) return;
    
    // Check if credentials are configured before enabling
    if (!config.laas_mile_enabled && (!config.laas_mile_client_id || !config.has_private_key)) {
      setMessage({ type: 'error', text: t('settings:laasMile.credentialsRequired') });
      return;
    }
    
    setSaving(true);
    try {
      await api.put(`/laas-mile/${store.id}/config`, {
        laas_mile_enabled: !config.laas_mile_enabled
      });
      setConfig(prev => ({ ...prev, laas_mile_enabled: !prev.laas_mile_enabled }));
      setMessage({ type: 'success', text: config.laas_mile_enabled ? t('settings:laasMile.deactivated') : t('settings:laasMile.activated') });
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.error || t('settings:laasMile.toggleError') });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-surface-1 rounded-xl p-6 border border-line">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-8 h-8 text-t3 animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface-2 dark:bg-surface-1 rounded-xl border border-line overflow-hidden">
      {/* Header */}
      <div 
        className="p-4 flex items-center justify-between cursor-pointer hover:bg-surface-3 dark:hover:bg-surface-2/50 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-4">
          <img src={efoodLogo} alt="Efood" className="w-10 h-10 object-contain" />
          <div>
            <h3 className="font-semibold text-t1">Efood Laas Mile</h3>
            <p className="text-sm text-t3">{t('settings:laasMile.subtitle')}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {config.laas_mile_enabled ? (
            <span className="flex items-center gap-1 text-green-600 text-sm font-medium">
              <CheckCircle className="w-4 h-4" />
              {t('common:active')}
            </span>
          ) : (
            <span className="flex items-center gap-1 text-t4 text-sm">
              <XCircle className="w-4 h-4" />
              {t('common:inactive')}
            </span>
          )}
          <svg 
            className={`w-5 h-5 text-t3 transition-transform ${expanded ? 'rotate-180' : ''}`} 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      {/* Expanded Content */}
      {expanded && (
        <div className="border-t border-line p-6 space-y-6">
          {/* Enable Toggle */}
          <div className="flex items-center justify-between p-4 bg-surface-2 dark:bg-surface-2/50 rounded-lg">
            <div>
              <p className="font-medium text-t1">{t('settings:laasMile.enableTitle')}</p>
              <p className="text-sm text-t3">{t('settings:laasMile.enableDesc')}</p>
            </div>
            <button
              onClick={handleToggleEnabled}
              disabled={saving}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                config.laas_mile_enabled ? 'bg-green-500' : 'bg-surface-3'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  config.laas_mile_enabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Info Box */}
          <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-700 dark:text-blue-300">
                <p className="font-medium mb-1">{t('settings:laasMile.howToGetCredentials')}</p>
                <ol className="list-decimal list-inside space-y-1">
                  <li>{t('settings:laasMile.step1')}</li>
                  <li>{t('settings:laasMile.step2')}</li>
                  <li>{t('settings:laasMile.step3')}</li>
                  <li>{t('settings:laasMile.step4')}</li>
                </ol>
                <a 
                  href="https://www.e-food.gr/partners" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 mt-2 text-blue-600 hover:text-blue-800"
                >
                  <ExternalLink className="w-4 h-4" />
                  Efood Partners
                </a>
              </div>
            </div>
          </div>

          {/* Credentials Form */}
          <div className="space-y-4">
            <h4 className="font-medium text-t1 flex items-center gap-2">
              <Key className="w-4 h-4" />
              API Credentials
            </h4>

            {/* Client ID */}
            <div>
              <label className="block text-sm font-medium text-t2 mb-1">
                Client ID
              </label>
              <input
                type="text"
                value={config.laas_mile_client_id || ''}
                onChange={(e) => setConfig(prev => ({ ...prev, laas_mile_client_id: e.target.value }))}
                placeholder="pandago-gr-vendor-XXXXXX"
                className="w-full px-3 py-2 border border-line-strong rounded-lg bg-surface-2 text-t1 placeholder-t3 focus:ring-2 focus:ring-red-500 focus:border-red-500"
              />
            </div>

            {/* Key ID */}
            <div>
              <label className="block text-sm font-medium text-t2 mb-1">
                Key ID
              </label>
              <input
                type="text"
                value={config.laas_mile_key_id || ''}
                onChange={(e) => setConfig(prev => ({ ...prev, laas_mile_key_id: e.target.value }))}
                placeholder={t('settings:laasMile.keyIdPlaceholder')}
                className="w-full px-3 py-2 border border-line-strong rounded-lg bg-surface-2 text-t1 placeholder-t3 focus:ring-2 focus:ring-red-500 focus:border-red-500"
              />
            </div>

            {/* Scope */}
            <div>
              <label className="block text-sm font-medium text-t2 mb-1">
                Scope
              </label>
              <input
                type="text"
                value={config.laas_mile_scope || ''}
                onChange={(e) => setConfig(prev => ({ ...prev, laas_mile_scope: e.target.value }))}
                placeholder="pandago.api.gr.*"
                className="w-full px-3 py-2 border border-line-strong rounded-lg bg-surface-2 text-t1 placeholder-t3 focus:ring-2 focus:ring-red-500 focus:border-red-500"
              />
            </div>

            {/* Venue ID */}
            <div>
              <label className="block text-sm font-medium text-t2 mb-1">
                Venue ID (Client Vendor ID)
              </label>
              <input
                type="text"
                value={config.laas_mile_venue_id || ''}
                onChange={(e) => setConfig(prev => ({ ...prev, laas_mile_venue_id: e.target.value }))}
                placeholder={t('settings:laasMile.venueIdPlaceholder')}
                className="w-full px-3 py-2 border border-line-strong rounded-lg bg-surface-2 text-t1 placeholder-t3 focus:ring-2 focus:ring-red-500 focus:border-red-500"
              />
              <p className="mt-1 text-xs text-t4">
                {t('settings:laasMile.venueIdHint')}
              </p>
            </div>

            {/* Private Key */}
            <div>
              <label className="block text-sm font-medium text-t2 mb-1">
                Private Key (PEM format)
                {config.has_private_key && (
                  <span className="ml-2 text-green-600 text-xs font-normal">✓ {t('settings:laasMile.privateKeySaved')}</span>
                )}
              </label>
              <div className="relative">
                <textarea
                  value={privateKey}
                  onChange={(e) => setPrivateKey(e.target.value)}
                  placeholder={config.has_private_key
                    ? t('settings:laasMile.privateKeyKeepPlaceholder')
                    : "-----BEGIN RSA PRIVATE KEY-----\n...\n-----END RSA PRIVATE KEY-----"
                  }
                  rows={4}
                  className={`w-full px-3 py-2 border border-line-strong rounded-lg bg-surface-2 text-t1 placeholder-t3 focus:ring-2 focus:ring-red-500 focus:border-red-500 font-mono text-sm ${
                    !showPrivateKey && privateKey ? 'text-security-disc' : ''
                  }`}
                  style={!showPrivateKey && privateKey ? { WebkitTextSecurity: 'disc' } as any : {}}
                />
                {privateKey && (
                  <button
                    type="button"
                    onClick={() => setShowPrivateKey(!showPrivateKey)}
                    className="absolute right-2 top-2 p-1 text-t3 hover:text-t2"
                  >
                    {showPrivateKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                )}
              </div>
              <p className="text-xs text-t4 mt-1">
                {t('settings:laasMile.privateKeyHint')}
              </p>
            </div>

            {/* RSA Key Generation Instructions */}
            <div className="p-4 bg-surface-2 dark:bg-surface-2/50 rounded-lg">
              <p className="text-sm font-medium text-t2 mb-2">{t('settings:laasMile.rsaKeyGeneration')}</p>
              <code className="block text-xs bg-surface-0 text-green-600 dark:text-green-400 p-3 rounded overflow-x-auto">
                {`${t('settings:laasMile.rsaCreatePrivateKeyComment')}\nopenssl genrsa -out private_key.pem 2048\n\n${t('settings:laasMile.rsaExportPublicKeyComment')}\nopenssl rsa -in private_key.pem -pubout -out public_key.pem`}
              </code>
            </div>
          </div>

          {/* Message */}
          {message && (
            <div className={`p-4 rounded-lg flex items-center gap-2 ${
              message.type === 'success' ? 'bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400' : 'bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400'
            }`}>
              {message.type === 'success' ? (
                <CheckCircle className="w-5 h-5" />
              ) : (
                <AlertCircle className="w-5 h-5" />
              )}
              {message.text}
            </div>
          )}

          {/* Save Button */}
          <div className="flex justify-end pt-4 border-t border-line">
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {t('common:saving')}
                </>
              ) : (
                <>
                  <Shield className="w-4 h-4" />
                  {t('settings:laasMile.saveCredentials')}
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LaasMileConfig;
